import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";

const VendorPaymentCreate = () => {
  const navigate = useNavigate();
  const [paymentEntries, setPaymentEntries] = useState([
    {
      id: 1,
      date: new Date(),
      reference_no: "",
      vendor_id: "",
      bank_id: "",
      amount: "",
      description: "",
    },
  ]);
  const [error, setError] = useState({});
  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankBalances, setBankBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState({});
  const [balanceErrors, setBalanceErrors] = useState({});

  useEffect(() => {
    Promise.all([Http.get("/vendor"), Http.get("/accounts/banks")])
      .then(([vendorsRes, banksRes]) => {
        setVendors(vendorsRes.data);
        setBanks(banksRes.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const fetchBankBalance = (bankId, entryId) => {
    if (!bankId) {
      setBankBalances((prev) => ({ ...prev, [entryId]: null }));
      setBalanceErrors((prev) => ({ ...prev, [entryId]: "" }));
      return;
    }

    setLoadingBalances((prev) => ({ ...prev, [entryId]: true }));
    setBalanceErrors((prev) => ({ ...prev, [entryId]: "" }));

    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalances((prev) => ({ ...prev, [entryId]: res.data.data }));
        } else {
          setBalanceErrors((prev) => ({
            ...prev,
            [entryId]: "Failed to fetch bank balance",
          }));
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceErrors((prev) => ({
          ...prev,
          [entryId]: "Error fetching bank balance",
        }));
        setBankBalances((prev) => ({ ...prev, [entryId]: null }));
      })
      .finally(() => {
        setLoadingBalances((prev) => ({ ...prev, [entryId]: false }));
      });
  };

  const addPaymentEntry = () => {
    const newId = Math.max(...paymentEntries.map((entry) => entry.id)) + 1;
    const newEntry = {
      id: newId,
      date: new Date(),
      reference_no: "",
      vendor_id: "",
      bank_id: "",
      amount: "",
      description: "",
    };
    setPaymentEntries([...paymentEntries, newEntry]);
  };

  const removePaymentEntry = (entryId) => {
    if (paymentEntries.length > 1) {
      setPaymentEntries(paymentEntries.filter((entry) => entry.id !== entryId));
      // Clean up related state
      setBankBalances((prev) => {
        const newBalances = { ...prev };
        delete newBalances[entryId];
        return newBalances;
      });
      setLoadingBalances((prev) => {
        const newLoading = { ...prev };
        delete newLoading[entryId];
        return newLoading;
      });
      setBalanceErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[entryId];
        return newErrors;
      });
    }
  };

  const handleChange = (e, entryId) => {
    const name = e.target.name;
    const value = e.target.value;
    setPaymentEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, [name]: value } : entry
      )
    );

    // Fetch bank balance when bank is selected
    if (name === "bank_id") {
      fetchBankBalance(value, entryId);
    }
  };

  const handleDateChange = (e, entryId) => {
    setPaymentEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, date: e.value } : entry
      )
    );
  };

  const handleSelectChange = (selectedOption, fieldName, entryId) => {
    setPaymentEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId
          ? { ...entry, [fieldName]: selectedOption.value }
          : entry
      )
    );

    // Fetch bank balance when bank is selected
    if (fieldName === "bank_id") {
      fetchBankBalance(selectedOption.value, entryId);
    }
  };

  const formSubmit = () => {
    // Format dates and prepare submission data
    const submissionData = paymentEntries.map((entry) => ({
      ...entry,
      // Adjust the date to local timezone and format as YYYY-MM-DD
      date: new Date(
        entry.date.getTime() - entry.date.getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0],
    }));

    // If only one entry, send as single object; otherwise send as array
    const dataToSend =
      submissionData.length === 1 ? submissionData[0] : submissionData;

    // clear any previous errors before submit
    setError({});

    Http.post("/accounts/vendor-payments", dataToSend)
      .then((res) => {
        navigate("/vendor-payments", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        const apiData = e && e.response && e.response.data ? e.response.data : e;
        const normalized =
          (apiData && (apiData.message || apiData.errors)) || apiData || {};
        setError(normalized);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Vendor Payment</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Payment Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {paymentEntries.map((entry, index) => (
              <div key={entry.id} className="payment-entry-form">
                {paymentEntries.length > 1 && (
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-primary mb-0">
                      Payment Entry #{index + 1}
                    </h6>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removePaymentEntry(entry.id)}
                      title="Remove this entry"
                    >
                      <i className="fa fa-trash"></i> Remove
                    </button>
                  </div>
                )}

                <div className="row">
                  <div className="col-lg">
                    <label>
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <Calendar
                      value={entry.date}
                      onChange={(e) => handleDateChange(e, entry.id)}
                      dateFormat="dd/mm/yy"
                      className="w-100"
                    />
                  </div>
                  <div className="col-lg">
                    <label>Reference No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="reference_no"
                      value={entry.reference_no || ""}
                      onChange={(e) => handleChange(e, entry.id)}
                      placeholder="Enter Reference Number"
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Vendor <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="vendor_id"
                      value={
                        vendors
                          .map((vendor) => ({
                            value: vendor.id,
                            label: `${vendor.name}`,
                          }))
                          .find((option) => option.value === entry.vendor_id) ||
                        null
                      }
                      options={vendors.map((vendor) => ({
                        value: vendor.id,
                        label: `${vendor.name}`,
                      }))}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "vendor_id", entry.id)
                      }
                    />
                  </div>
                  <div className="col-lg">
                    <label>
                      Bank <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      name="bank_id"
                      value={
                        banks
                          .map((bank) => ({
                            value: bank.id,
                            label: `${bank.name} - ${bank.account_number}`,
                          }))
                          .find((option) => option.value === entry.bank_id) ||
                        null
                      }
                      options={banks.map((bank) => ({
                        value: bank.id,
                        label: `${bank.name} - ${bank.account_number}`,
                      }))}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "bank_id", entry.id)
                      }
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg"></div>
                  <div className="col-lg">
                    {/* Bank Balance Display */}
                    {entry.bank_id && (
                      <div
                        className="alert alert-info"
                        style={{ marginBottom: "15px" }}
                      >
                        <strong>Bank Balance:</strong>
                        {loadingBalances[entry.id] ? (
                          <div className="d-flex align-items-center mt-2">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                            <span>Loading balance...</span>
                          </div>
                        ) : balanceErrors[entry.id] ? (
                          <div className="text-danger mt-2">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {balanceErrors[entry.id]}
                          </div>
                        ) : bankBalances[entry.id] ? (
                          <div className="mt-2">
                            <div className="row">
                              <div className="col-md-4">
                                <small className="text-muted">Bank Name:</small>
                                <div className="font-weight-bold">
                                  {bankBalances[entry.id].name}
                                </div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">
                                  Account Number:
                                </small>
                                <div className="font-weight-bold">
                                  {bankBalances[entry.id].account_number}
                                </div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">
                                  Available Balance:
                                </small>
                                <div className="font-weight-bold text-info">
                                  ৳ {parseFloat(bankBalances[entry.id].total_balance).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={entry.amount || ""}
                      onChange={(e) => handleChange(e, entry.id)}
                      placeholder="Enter Amount"
                    />
                  </div>
                  <div className="col-lg">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={entry.description || ""}
                      onChange={(e) => handleChange(e, entry.id)}
                      placeholder="Enter Description"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                {/* Add separator between entries */}
                {index < paymentEntries.length - 1 && (
                  <hr className="my-4" style={{ borderColor: "#ddd" }} />
                )}
              </div>
            ))}

            {/* Add More Button */}
            <div className="row mt-4">
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addPaymentEntry}
                >
                  <i className="fa fa-plus mg-r-5"></i> Add More Payment Entry
                </button>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-3">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-save mg-r-10"></i>{" "}
                  {paymentEntries.length > 1
                    ? `Save ${paymentEntries.length} Payments`
                    : "Save Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentCreate;
